import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'underscore';
import { getFilteredCampaignTypeFromRawFilter, getLevelItems, SelectorLevels, CampaignType } from '@bingads-webui-campaign-react/shell-ui-common';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { getLevel, setLevel, TargetingLevel } from '@bingads-webui-campaign/targeting-settings-scaffolds';
import { withCurrentExpandedScope } from '@bingads-webui-campaign-react/use-current-scope-entity';
import { isXandrServing } from '@bingads-webui-campaign-react/constants';
import { navigateToTargetLevel } from './utils';

const withCheckCampaign = (LevelSelectorWrapper) => {
  class CheckCampaignWrapper extends React.PureComponent {
    static propTypes = {
      scope: PropTypes.shape({
        values: {
          adGroupId: PropTypes.number,
          campaignId: PropTypes.number,
        },
      }),
      i18n: PropTypes.shape({
        getString: PropTypes.func,
      }).isRequired,
      urls: PropTypes.arrayOf(PropTypes.string).isRequired,
      currentCampaign: PropTypes.shape({}).isRequired,
    };

    static defaultProps = {
      scope: {},
    };

    constructor(props) {
      super(props);
      const {
        i18n,
      } = props;
      this.levelItems = getLevelItems({ i18n });
      this.checkCampaign();
    }

    componentDidUpdate(prevProp) {
      if ((prevProp.currentCampaign !== this.props.currentCampaign) ||
        getFilteredCampaignTypeFromRawFilter(prevProp[WaterfallFilterType.CampaignType]) !==
        getFilteredCampaignTypeFromRawFilter(this.props[WaterfallFilterType.CampaignType])) {
        this.checkCampaign();
        this.forceUpdate();
      }
    }

    isSmartShoppingCampaign() {
      const { currentCampaign } = this.props;
      return currentCampaign && currentCampaign.CampaignSubType === 'ShoppingSmartAds';
    }

    isHotelCampaign() {
      const { currentCampaign } = this.props;
      return currentCampaign && currentCampaign.CampaignType === 'Hotel';
    }

    isHotelCampaignType() {
      const selectedCampaignType = getFilteredCampaignTypeFromRawFilter(this.props[WaterfallFilterType.CampaignType]);
      return selectedCampaignType === CampaignType.Hotel;
    }

    isPmaxCampaign() {
      const { currentCampaign } = this.props;
      return currentCampaign && currentCampaign.CampaignType === 'PerformanceMax';
    }

    isPmaxCampaignType() {
      const selectedCampaignType = getFilteredCampaignTypeFromRawFilter(this.props[WaterfallFilterType.CampaignType]);
      return selectedCampaignType === CampaignType.PerformanceMax;
    }

    isAppCampaign() {
      const { currentCampaign } = this.props;
      return currentCampaign && currentCampaign.CampaignType === 'App';
    }

    isAppCampaignType() {
      const selectedCampaignType = getFilteredCampaignTypeFromRawFilter(this.props[WaterfallFilterType.CampaignType]);
      return selectedCampaignType === CampaignType.App;
    }

    isXandrCampaign() {
      const { currentCampaign } = this.props;
      return currentCampaign && isXandrServing(currentCampaign.CampaignSubType, currentCampaign.CampaignSettings);
    }

    isLocation() {
      const {
        urls,
      } = this.props;
      return urls && urls.length && (urls[0].includes('/locations/'));
    }

    isExcludedLocation() {
      const {
        urls,
      } = this.props;
      return urls && urls.length && (urls[0].includes('/excluded/'));
    }

    isAdSchedule() {
      const {
        urls,
      } = this.props;
      return urls && urls.length && urls[0].includes('/adschedule/');
    }

    isDevices() {
      const {
        urls,
      } = this.props;
      return urls && urls.length && urls[0].includes('/devices');
    }

    forceToCampaign() {
      const {
        scope,
        urls,
      } = this.props;
      const selectedLevel = getLevel(scope.values.adGroupId, true);
      if (selectedLevel !== TargetingLevel.Campaign) {
        setLevel(TargetingLevel.Campaign, true);
        navigateToTargetLevel(TargetingLevel.Campaign, scope, urls);
      }
      this.levelItems = this.levelItems.filter(item => item.value === SelectorLevels.Campaign);
    }

    forceToAdGroup() {
      const {
        scope,
        urls,
      } = this.props;
      const selectedLevel = getLevel(scope.values.adGroupId, true);
      if (selectedLevel !== TargetingLevel.AdGroup) {
        setLevel(TargetingLevel.AdGroup, true);
        navigateToTargetLevel(TargetingLevel.AdGroup, scope, urls);
      }
      this.levelItems = this.levelItems.filter(item => item.value === SelectorLevels.AdGroup);
    }

    isOnlyCampaignLevelSupported() {
      const isSmartShoppingCampaign = this.isSmartShoppingCampaign();
      const isHotelCampaign = this.isHotelCampaign();
      const isAssetGroupBasedCampaign = this.isPmaxCampaign() || this.isAppCampaign();
      const isAssetGroupBasedCampaignType = this.isPmaxCampaignType() || this.isAppCampaignType();
      if (isSmartShoppingCampaign) {
        return true;
      } else if (isHotelCampaign && (this.isAdSchedule() || this.isLocation() || this.isExcludedLocation())) {
        return true;
      } else if (this.isHotelCampaignType() && (this.isAdSchedule() || this.isLocation() || this.isExcludedLocation())) {
        return true;
      } else if (isAssetGroupBasedCampaign && (this.isAdSchedule() || this.isLocation() || this.isExcludedLocation() || this.isDevices())) {
        return true;
      } else if (isAssetGroupBasedCampaignType && (this.isAdSchedule() || this.isLocation() || this.isExcludedLocation() || this.isDevices())) {
        return true;
      }
      return false;
    }

    isOnlyAdGroupLevelSupported() {
      if (this.isXandrCampaign()) {
        return true;
      }
      return false;
    }

    checkCampaign() {
      const {
        i18n,
      } = this.props;
      if (this.isOnlyCampaignLevelSupported()) {
        this.forceToCampaign();
      } else if (this.isOnlyAdGroupLevelSupported()) {
        this.forceToAdGroup();
      } else {
        this.levelItems = getLevelItems({ i18n });
      }
    }

    render() {
      return (
        <LevelSelectorWrapper
          levelItems={this.levelItems}
          {...this.props}
        />
      );
    }
  }
  return CheckCampaignWrapper;
};

export const withCheckCampaignWithScope = compose(withCurrentExpandedScope, withCheckCampaign);
